import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { PX_PER_CM } from '../../core/CanvasManager';
import eventBus from '../../core/EventBus';
import useIsMobile from '../../hooks/useIsMobile';

const RULER_SIZE = 24;
const RULER_GAP = 6; // gap between ruler and canvas

function RulerH({ widthCm, zoom, onDragStart }) {
  const cmPx = PX_PER_CM * zoom;
  const totalPx = Math.round(widthCm * cmPx);
  let step = 1;
  if (cmPx < 6) step = 20;
  else if (cmPx < 10) step = 10;
  else if (cmPx < 22) step = 5;
  const ticks = [];
  for (let cm = 0; cm <= widthCm; cm += step) {
    const x = cm * cmPx;
    const isMajor = cm % (step >= 5 ? step : 10) === 0;
    const tickH = isMajor ? 12 : 5;
    ticks.push(
      <g key={cm}>
        <line x1={x} y1={RULER_SIZE - tickH} x2={x} y2={RULER_SIZE} stroke={isMajor ? '#94a3b8' : '#cbd5e1'} strokeWidth={isMajor ? 1 : 0.5} />
        {isMajor && cm > 0 && (
          <text x={x} y={9} fill="#64748b" fontSize="9" fontFamily="system-ui, sans-serif" textAnchor="middle" dominantBaseline="central" fontWeight="500">{cm}</text>
        )}
      </g>
    );
  }
  return (
    <svg
      width={totalPx}
      height={RULER_SIZE}
      style={{ display: 'block', overflow: 'visible', cursor: 'grab' }}
      onMouseDown={(e) => { e.preventDefault(); if (onDragStart) onDragStart(e, 'horizontal'); }}
    >
      <rect width="100%" height="100%" fill="#f1f3f5" />
      {ticks}
      <line x1={0} y1={RULER_SIZE - 0.5} x2={totalPx} y2={RULER_SIZE - 0.5} stroke="#e2e8f0" strokeWidth={0.5} />
    </svg>
  );
}

function RulerV({ heightCm, zoom, onDragStart }) {
  const cmPx = PX_PER_CM * zoom;
  const totalPx = Math.round(heightCm * cmPx);
  let step = 1;
  if (cmPx < 6) step = 20;
  else if (cmPx < 10) step = 10;
  else if (cmPx < 22) step = 5;
  const ticks = [];
  for (let cm = 0; cm <= heightCm; cm += step) {
    const y = cm * cmPx;
    const isMajor = cm % (step >= 5 ? step : 10) === 0;
    const tickW = isMajor ? 12 : 5;
    ticks.push(
      <g key={cm}>
        <line y1={y} x1={RULER_SIZE - tickW} y2={y} x2={RULER_SIZE} stroke={isMajor ? '#94a3b8' : '#cbd5e1'} strokeWidth={isMajor ? 1 : 0.5} />
        {isMajor && cm > 0 && (
          <text x={9} y={y} fill="#64748b" fontSize="9" fontFamily="system-ui, sans-serif" textAnchor="middle" dominantBaseline="central" fontWeight="500" transform={`rotate(-90, 9, ${y})`}>{cm}</text>
        )}
      </g>
    );
  }
  return (
    <svg
      width={RULER_SIZE}
      height={totalPx}
      style={{ display: 'block', overflow: 'visible', cursor: 'grab' }}
      onMouseDown={(e) => { e.preventDefault(); if (onDragStart) onDragStart(e, 'vertical'); }}
    >
      <rect width="100%" height="100%" fill="#f1f3f5" />
      {ticks}
      <line x1={RULER_SIZE - 0.5} y1={0} x2={RULER_SIZE - 0.5} y2={totalPx} stroke="#e2e8f0" strokeWidth={0.5} />
    </svg>
  );
}

/**
 * Temporary visual guideline indicator shown while dragging from a ruler.
 */
function GuideDragIndicator({ orientation, position, canvasRect }) {
  if (!canvasRect) return null;
  const style = {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: 9999,
    background: 'none',
  };
  if (orientation === 'horizontal') {
    return (
      <div style={{
        ...style,
        left: canvasRect.left,
        top: position - 0.5,
        width: canvasRect.width,
        height: 0,
        borderTop: '2px dashed #2196F3',
      }} />
    );
  }
  return (
    <div style={{
      ...style,
      left: position - 0.5,
      top: canvasRect.top,
      width: 0,
      height: canvasRect.height,
      borderLeft: '2px dashed #2196F3',
    }} />
  );
}

function GridOverlay({ widthCm, heightCm, displayZoom, scaledW, scaledH }) {
  const maxDimCm = Math.max(widthCm, heightCm);
  let minorCm, majorCm;
  if (maxDimCm > 150) { minorCm = 10; majorCm = 50; }
  else if (maxDimCm > 60) { minorCm = 5; majorCm = 10; }
  else { minorCm = 1; majorCm = 5; }

  const minorPx = minorCm * PX_PER_CM * displayZoom;
  const majorPx = majorCm * PX_PER_CM * displayZoom;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        width: scaledW,
        height: scaledH,
        pointerEvents: 'none',
        zIndex: 2,
        backgroundImage: [
          `repeating-linear-gradient(90deg, rgba(160,168,185,0.3) 0px, rgba(160,168,185,0.3) 1px, transparent 1px, transparent ${minorPx}px)`,
          `repeating-linear-gradient(0deg, rgba(160,168,185,0.3) 0px, rgba(160,168,185,0.3) 1px, transparent 1px, transparent ${minorPx}px)`,
          `repeating-linear-gradient(90deg, rgba(120,125,140,0.5) 0px, rgba(120,125,140,0.5) 1.5px, transparent 1.5px, transparent ${majorPx}px)`,
          `repeating-linear-gradient(0deg, rgba(120,125,140,0.5) 0px, rgba(120,125,140,0.5) 1.5px, transparent 1.5px, transparent ${majorPx}px)`,
        ].join(', '),
      }}
    />
  );
}

function CanvasArea({ zoom, onZoomChange, previewMode, widthCm, heightCm, bleedCm = 0, hemCm = 0, hemPerSide = {}, hohlsaum = {}, overlayColors = {}, canvasManager, showRulers = false, gridVisible = false }) {
  const isMobile = useIsMobile();
  const bleedColor = overlayColors.bleedColor || '#ef4444';
  const hemColor = overlayColors.hemColor || '#3b82f6';
  const tunnelColor = '#7c3aed';
  const containerRef = useRef(null);
  const prevDimsRef = useRef({ w: widthCm, h: heightCm });
  const [rotationAngle, setRotationAngle] = useState(null);
  const rotationTimerRef = useRef(null);
  const fitZoomRef = useRef(0.2); // cached fit-zoom level
  const [scrollPos, setScrollPos] = useState({ left: 0, top: 0 }); // for navigator
  const [guideDrag, setGuideDrag] = useState(null); // { orientation, mousePos }
  const canvasWrapperRef = useRef(null); // ref to the scaled canvas wrapper div

  // ── Guide drag from ruler ──────────────────────────────────────────────────
  const handleRulerDragStart = useCallback((e, orientation) => {
    if (!canvasManager) return;
    e.preventDefault();
    const startPos = orientation === 'horizontal' ? e.clientY : e.clientX;
    setGuideDrag({ orientation, mousePos: startPos });

    const onMove = (me) => {
      const pos = orientation === 'horizontal' ? me.clientY : me.clientX;
      setGuideDrag({ orientation, mousePos: pos });
    };

    const onUp = (ue) => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      setGuideDrag(null);

      // Calculate position relative to the canvas
      const wrapper = canvasWrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const displayZoomVal = Math.max(0.02, zoom || 0.2);

      if (orientation === 'horizontal') {
        const relY = ue.clientY - rect.top;
        if (relY < 0 || relY > rect.height) return; // dropped outside canvas
        const canvasPxY = relY / displayZoomVal; // convert screen px to virtual canvas px
        canvasManager.addGuide('horizontal', canvasPxY);
      } else {
        const relX = ue.clientX - rect.left;
        if (relX < 0 || relX > rect.width) return; // dropped outside canvas
        const canvasPxX = relX / displayZoomVal;
        canvasManager.addGuide('vertical', canvasPxX);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [canvasManager, zoom]);

  // ── Guide delete on right-click or double-click ────────────────────────────
  useEffect(() => {
    if (!canvasManager || !canvasManager.canvas) return;
    const canvas = canvasManager.canvas;

    const handleDblClick = (opt) => {
      const pointer = canvas.getPointer(opt.e);
      const guide = canvasManager.getGuideAtPoint(pointer.x, pointer.y);
      if (guide) {
        canvasManager.removeGuide(guide);
      }
    };

    const handleContextMenu = (opt) => {
      const pointer = canvas.getPointer(opt.e);
      const guide = canvasManager.getGuideAtPoint(pointer.x, pointer.y);
      if (guide) {
        opt.e.preventDefault();
        opt.e.stopPropagation();
        canvasManager.removeGuide(guide);
      }
    };

    canvas.on('mouse:dblclick', handleDblClick);
    const handleMouseDown = (opt) => {
      if (opt.e.button === 2) { // right-click
        handleContextMenu(opt);
      }
    };
    canvas.on('mouse:down', handleMouseDown);

    return () => {
      canvas.off('mouse:dblclick', handleDblClick);
      canvas.off('mouse:down', handleMouseDown);
    };
  }, [canvasManager]);

  useEffect(() => {
    const onRotating = ({ angle }) => {
      setRotationAngle(angle);
      clearTimeout(rotationTimerRef.current);
    };
    const onRotateEnd = () => {
      rotationTimerRef.current = setTimeout(() => setRotationAngle(null), 800);
    };
    const unsub1 = eventBus.on('object:rotating', onRotating);
    const unsub2 = eventBus.on('object:rotate-end', onRotateEnd);
    return () => { unsub1(); unsub2(); clearTimeout(rotationTimerRef.current); };
  }, []);

  const calculateFitZoom = useCallback(() => {
    if (!containerRef.current) return 0.2;
    const containerW = containerRef.current.clientWidth;
    const containerH = containerRef.current.clientHeight;
    if (containerW < 10 || containerH < 10) return 0.2;
    const canvasW = widthCm * PX_PER_CM;
    const canvasH = heightCm * PX_PER_CM;
    // Mobile: Canvas soll Abstand zur Tab-Leiste haben
    const isMobileView = window.innerWidth < 768;
    const paddingX = isMobileView ? 20 : RULER_SIZE + 80;
    const paddingY = isMobileView ? 60 : RULER_SIZE + 80;
    const scaleX = (containerW - paddingX) / canvasW;
    const scaleY = (containerH - paddingY) / canvasH;
    const fit = Math.max(0.02, Math.min(scaleX, scaleY, 2));
    fitZoomRef.current = fit;
    return fit;
  }, [widthCm, heightCm]);

  // Center the scroll container so canvas is visible in the middle
  const centerScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    requestAnimationFrame(() => {
      const scrollW = container.scrollWidth - container.clientWidth;
      const scrollH = container.scrollHeight - container.clientHeight;
      if (scrollW > 0) container.scrollLeft = scrollW / 2;
      if (scrollH > 0) container.scrollTop = scrollH / 2;
    });
  }, []);

  // Track whether user has manually zoomed — if so, don't auto-fit on resize
  const userZoomedRef = useRef(false);
  const initialFitDoneRef = useRef(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const resizeTimerRef = useRef(null);

  // zoom === 0 means "fit please" (from Einpassen button or dimension change)
  useEffect(() => {
    const prev = prevDimsRef.current;
    const dimsChanged = prev.w !== widthCm || prev.h !== heightCm;
    prevDimsRef.current = { w: widthCm, h: heightCm };
    if (dimsChanged || zoom === 0) {
      userZoomedRef.current = false;
      const fit = calculateFitZoom();
      if (onZoomChange && fit > 0) {
        onZoomChange(fit);
        requestAnimationFrame(centerScroll);
      }
      if (!canvasReady && fit > 0) setCanvasReady(true);
      return;
    }
    // Only mark as user-zoomed after initial fit is done
    if (zoom > 0 && initialFitDoneRef.current) {
      userZoomedRef.current = true;
    }
  }, [widthCm, heightCm, zoom, calculateFitZoom, onZoomChange, centerScroll, canvasReady]);

  // Initial auto-fit on mount (single attempt — ResizeObserver handles subsequent fits)
  useEffect(() => {
    const doFit = () => {
      const fit = calculateFitZoom();
      if (onZoomChange && fit > 0) {
        onZoomChange(fit);
        initialFitDoneRef.current = true;
        setCanvasReady(true);
        requestAnimationFrame(centerScroll);
      }
    };
    const t1 = setTimeout(doFit, 300);
    return () => { clearTimeout(t1); };
  }, []); // eslint-disable-line

  // Re-fit on genuine window/layout resize — debounced, only if user hasn't manually zoomed
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      clearTimeout(resizeTimerRef.current);
      resizeTimerRef.current = setTimeout(() => {
        if (userZoomedRef.current) {
          calculateFitZoom();
          return;
        }
        const fit = calculateFitZoom();
        if (onZoomChange && fit > 0) onZoomChange(fit);
      }, 150);
    });
    observer.observe(containerRef.current);
    return () => { observer.disconnect(); clearTimeout(resizeTimerRef.current); };
  }, [calculateFitZoom, onZoomChange]);

  const isZoomedIn = zoom > fitZoomRef.current + 0.01;

  // Track scroll position for navigator viewport indicator
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onScroll = () => {
      setScrollPos({ left: container.scrollLeft, top: container.scrollTop });
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.03 : 0.03;
        const newZoom = Math.max(0.02, Math.min(3, zoom + delta));
        if (onZoomChange) onZoomChange(newZoom);
      }
    };
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [zoom, onZoomChange]);

  // Pinch-to-zoom for mobile
  useEffect(() => {
    const container = containerRef.current;
    if (!container || window.innerWidth >= 768) return;

    let initialDistance = 0;
    let initialZoom = zoom;

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        initialDistance = getDistance(e.touches);
        initialZoom = zoom;
      }
    };

    const handleTouchMove = (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const currentDistance = getDistance(e.touches);
        const scale = currentDistance / initialDistance;
        const newZoom = Math.max(0.05, Math.min(3, initialZoom * scale));
        if (onZoomChange) onZoomChange(newZoom);
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, [zoom, onZoomChange]);

  const canvasWidth = Math.round(widthCm * PX_PER_CM);   // virtual full-res size
  const canvasHeight = Math.round(heightCm * PX_PER_CM);
  const internalScale = canvasManager?.internalScale || 1; // ratio of actual DOM canvas to virtual
  const actualCanvasW = Math.round(canvasWidth * internalScale);  // actual DOM canvas size
  const actualCanvasH = Math.round(canvasHeight * internalScale);
  const displayZoom = Math.max(0.02, zoom || 0.2);         // user-facing zoom (relative to virtual size)
  const cssScale = displayZoom / internalScale;             // CSS scale to apply on the DOM canvas
  const scaledW = Math.round(canvasWidth * displayZoom);    // visual display size
  const scaledH = Math.round(canvasHeight * displayZoom);

  // Controls an Zoom anpassen — damit sie auf dem Bildschirm immer gleich groß bleiben
  useEffect(() => {
    if (canvasManager) {
      canvasManager.updateControlSizeForZoom(displayZoom);
    }
  }, [displayZoom, canvasManager]);

  const legendItems = useMemo(() => {
    const items = [];
    const sl = { top: 'oben', bottom: 'unten', left: 'links', right: 'rechts' };
    if (bleedCm > 0) items.push({ color: bleedColor, label: `Beschnitt: ${bleedCm} cm`, icon: 'scissors' });
    const hp = hemPerSide || {};
    const hs = ['top', 'bottom', 'left', 'right'].filter(s => (hp[s] || 0) > 0);
    if (hs.length > 0) {
      const allSame = hs.every(s => hp[s] === hp[hs[0]]);
      if (allSame && hs.length === 4) {
        items.push({ color: hemColor, label: `Saum: ${hp[hs[0]]} cm` });
      } else {
        hs.forEach(s => items.push({ color: hemColor, label: `Saum ${sl[s]}: ${hp[s]} cm` }));
      }
    }
    ['top', 'bottom', 'left', 'right'].forEach(s => {
      if ((hohlsaum[s] || 0) > 0) items.push({ color: tunnelColor, label: `Hohlsaum ${sl[s]}: ${hohlsaum[s]} cm` });
    });
    return items;
  }, [bleedCm, bleedColor, hemPerSide, hemColor, hohlsaum, tunnelColor]);

  // Compute canvas rect for the drag indicator
  const canvasRect = canvasWrapperRef.current ? canvasWrapperRef.current.getBoundingClientRect() : null;

  return (
    <div className="flex-1 relative" style={{ overflow: 'hidden' }}>
      {/* Guide drag indicator — fixed overlay while dragging from ruler */}
      {guideDrag && (
        <GuideDragIndicator
          orientation={guideDrag.orientation}
          position={guideDrag.mousePos}
          canvasRect={canvasRect}
        />
      )}
      {/* Dimension info — floats above the scroll area */}
      <div className="absolute top-2 left-0 right-0 z-10 flex items-center justify-center gap-1.5 pointer-events-none">
        <span className="text-[11px] font-medium text-gray-600 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-md shadow-sm border border-gray-200/60">
          {widthCm} &times; {heightCm} cm
        </span>
        <span className="text-[10px] text-gray-400 bg-white/80 backdrop-blur-sm px-2 py-1 rounded-md shadow-sm border border-gray-200/60">
          {canvasWidth} &times; {canvasHeight} px
        </span>
      </div>

      {/* Rotation angle indicator */}
      {rotationAngle !== null && (
        <div className="absolute top-12 left-0 right-0 z-20 flex items-center justify-center pointer-events-none animate-fadeIn">
          <div className="bg-gray-900/85 backdrop-blur-sm text-white px-4 py-1.5 rounded-full shadow-lg flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="opacity-70">
              <path d="M21 12a9 9 0 1 1-9-9"/><path d="M21 3v6h-6"/>
            </svg>
            <span className="text-sm font-semibold tabular-nums">{rotationAngle}°</span>
          </div>
        </div>
      )}

      {/* Legend — floats bottom-left above the scroll area */}
      {legendItems.length > 0 && (
        <div className="absolute bottom-2 left-2 z-10 pointer-events-none">
          <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200/80 px-3 py-2 flex flex-col gap-1.5 animate-legendIn">
            {legendItems.map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                {item.icon === 'scissors' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={item.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/>
                  </svg>
                ) : (
                  <svg width="20" height="2" style={{ flexShrink: 0 }}>
                    <line x1="0" y1="1" x2="20" y2="1" stroke={item.color} strokeWidth="2" strokeDasharray="4 2" />
                  </svg>
                )}
                <span className="text-[10px] font-medium whitespace-nowrap" style={{ color: item.color }}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scrollable canvas area */}
      <div
        ref={containerRef}
        className={`absolute inset-0 canvas-scroll-area ${isMobile ? 'overflow-y-auto overflow-x-hidden' : 'overflow-auto'}`}
        style={{
          background: '#e5e7eb',
          opacity: canvasReady ? 1 : 0,
          transition: canvasReady ? 'none' : 'none',
        }}
        onMouseDown={(e) => {
          eventBus.emit('toolbar:close-dialogs');
          const tag = e.target.tagName?.toLowerCase();
          if (tag !== 'canvas') {
            eventBus.emit('stage:deselect');
          }
        }}
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes('application/selenny-clipart')) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
          }
        }}
        onDrop={(e) => {
          const clipData = e.dataTransfer.getData('application/selenny-clipart');
          if (clipData && canvasManager) {
            e.preventDefault();
            try {
              const clip = JSON.parse(clipData);
              const apiBase = process.env.REACT_APP_API_URL || '/designer/api';
              const url = `${apiBase}${clip.url}`;
              const isSvg = clip.filename.toLowerCase().endsWith('.svg');
              if (isSvg) {
                canvasManager.addSVG(url, { name: clip.name });
              } else {
                canvasManager.addImage(url, { maxWidth: 200, name: clip.name });
              }
            } catch (err) { /* ignore */ }
          }
        }}
      >
      {/* Subtle noise texture overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        opacity: 0.035,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        backgroundSize: '200px 200px',
      }} />

      {/* Selenny Designer watermark logo */}
      <div className="absolute inset-0 pointer-events-none flex items-center justify-center" style={{ opacity: 0.07 }}>
        <div style={{ textAlign: 'center', userSelect: 'none' }}>
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none" style={{ margin: '0 auto 12px' }}>
            <rect x="4" y="12" width="24" height="32" rx="3" stroke="currentColor" strokeWidth="2.5" fill="none" style={{ color: '#374151' }} />
            <rect x="36" y="12" width="24" height="32" rx="3" stroke="currentColor" strokeWidth="2.5" fill="none" style={{ color: '#374151' }} />
            <path d="M16 20l-5 8 5 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#374151' }} />
            <path d="M48 20l5 8-5 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#374151' }} />
            <circle cx="32" cy="52" r="6" stroke="currentColor" strokeWidth="2.5" fill="none" style={{ color: '#374151' }} />
            <circle cx="32" cy="52" r="2" fill="currentColor" style={{ color: '#374151' }} />
          </svg>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '0.15em', color: '#374151', fontFamily: 'system-ui, -apple-system, sans-serif', textTransform: 'uppercase' }}>
            Selenny®
          </div>
          <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.35em', color: '#374151', fontFamily: 'system-ui, -apple-system, sans-serif', textTransform: 'uppercase', marginTop: 2 }}>
            Designer
          </div>
        </div>
      </div>

      {/* Inner flex wrapper: min-size=100% ensures centering when content is smaller, grows when content is larger */}
      <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'center', minWidth: '100%', minHeight: '100%', padding: isMobile ? '40px 10px 40px' : 40, position: 'relative', zIndex: 1 }}>
      {/* Outer wrapper with explicit size so rulers can attach */}
      <div style={{ position: 'relative', flexShrink: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.18), 0 1px 6px rgba(0,0,0,0.12)' }}>

        {/* Rulers — positioned relative to the scaled canvas wrapper */}
        {showRulers && (
          <>
            {/* Corner */}
            <div className="canvas-ruler-corner" style={{ position: 'absolute', left: -(RULER_SIZE + RULER_GAP), top: -(RULER_SIZE + RULER_GAP), width: RULER_SIZE, height: RULER_SIZE, background: '#f1f3f5', borderRight: '1px solid #d1d5db', borderBottom: '1px solid #d1d5db', borderRadius: '4px 0 0 0', zIndex: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 7, color: '#9ca3af' }}>cm</span>
            </div>
            {/* Top ruler — drag down to create horizontal guide */}
            <div className="canvas-ruler-area" style={{ position: 'absolute', left: 0, top: -(RULER_SIZE + RULER_GAP), width: scaledW + 20, height: RULER_SIZE, overflow: 'visible', zIndex: 5, clipPath: `inset(0 0 0 0)` }}>
              <RulerH widthCm={widthCm} zoom={displayZoom} onDragStart={handleRulerDragStart} />
            </div>
            {/* Left ruler — drag right to create vertical guide */}
            <div className="canvas-ruler-area" style={{ position: 'absolute', left: -(RULER_SIZE + RULER_GAP), top: 0, width: RULER_SIZE, height: scaledH + 20, overflow: 'visible', zIndex: 5, clipPath: `inset(0 0 0 0)` }}>
              <RulerV heightCm={heightCm} zoom={displayZoom} onDragStart={handleRulerDragStart} />
            </div>
          </>
        )}

        {/* Scaled canvas */}
        <div
          ref={canvasWrapperRef}
          style={{
            width: scaledW,
            height: scaledH,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              transform: `scale(${cssScale})`,
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          >
            <div
              className="relative"
              style={{
                width: actualCanvasW,
                height: actualCanvasH,
                boxShadow: '0 8px 32px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.08)',
                background: '#fff',
              }}
            >
              <canvas id="design-canvas" />
            </div>
          </div>
          {gridVisible && <GridOverlay widthCm={widthCm} heightCm={heightCm} displayZoom={displayZoom} scaledW={scaledW} scaledH={scaledH} />}
        </div>
      </div>
      </div>
      </div>

      {/* ── Navigator mini-map (Photoshop-style) — visible only when zoomed in ── */}
      {isZoomedIn && (
        <Navigator
          containerRef={containerRef}
          scrollPos={scrollPos}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          displayZoom={displayZoom}
        />
      )}
    </div>
  );
}

// ── Navigator: mini-map with draggable viewport rectangle ─────────────────────
function Navigator({ containerRef, scrollPos, canvasWidth, canvasHeight, displayZoom }) {
  const NAV_W = 180;
  const aspect = canvasHeight / canvasWidth;
  const NAV_H = Math.round(NAV_W * aspect);
  const navRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ mx: 0, my: 0, sl: 0, st: 0 });

  // Total scrollable content size (canvas + padding)
  const contentW = canvasWidth * displayZoom + 80;
  const contentH = canvasHeight * displayZoom + 80;

  const container = containerRef.current;
  const viewW = container ? container.clientWidth : contentW;
  const viewH = container ? container.clientHeight : contentH;

  // Scale factor: navigator pixels per content pixel
  const scaleX = NAV_W / contentW;
  const scaleY = NAV_H / contentH;

  // Viewport rectangle in navigator space
  const vpLeft = scrollPos.left * scaleX;
  const vpTop = scrollPos.top * scaleY;
  const vpW = Math.min(viewW * scaleX, NAV_W);
  const vpH = Math.min(viewH * scaleY, NAV_H);

  // Canvas rectangle in navigator space (centered with padding offset)
  const pad = 40; // half of the 80px padding
  const cvLeft = pad * scaleX;
  const cvTop = pad * scaleY;
  const cvW = canvasWidth * displayZoom * scaleX;
  const cvH = canvasHeight * displayZoom * scaleY;

  const handleNavMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!container) return;

    const rect = navRef.current.getBoundingClientRect();
    const nx = e.clientX - rect.left;
    const ny = e.clientY - rect.top;

    // If clicking outside the viewport rect, jump there first
    if (nx < vpLeft || nx > vpLeft + vpW || ny < vpTop || ny > vpTop + vpH) {
      const newScrollLeft = (nx - vpW / 2) / scaleX;
      const newScrollTop = (ny - vpH / 2) / scaleY;
      container.scrollLeft = Math.max(0, newScrollLeft);
      container.scrollTop = Math.max(0, newScrollTop);
    }

    setIsDragging(true);
    dragStartRef.current = {
      mx: e.clientX,
      my: e.clientY,
      sl: container.scrollLeft,
      st: container.scrollTop,
    };
  }, [container, vpLeft, vpTop, vpW, vpH, scaleX, scaleY]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      if (!container) return;
      const dx = e.clientX - dragStartRef.current.mx;
      const dy = e.clientY - dragStartRef.current.my;
      // Convert navigator pixels to scroll pixels
      container.scrollLeft = dragStartRef.current.sl + dx / scaleX;
      container.scrollTop = dragStartRef.current.st + dy / scaleY;
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, container, scaleX, scaleY]);

  return (
    <div
      ref={navRef}
      className="absolute z-20 rounded-lg shadow-lg border border-gray-300/80 overflow-hidden"
      style={{
        bottom: 12,
        right: 12,
        width: NAV_W,
        height: NAV_H,
        background: '#d1d5db',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
      onMouseDown={handleNavMouseDown}
    >
      {/* Canvas area representation */}
      <div
        style={{
          position: 'absolute',
          left: cvLeft,
          top: cvTop,
          width: cvW,
          height: cvH,
          background: '#ffffff',
          border: '1px solid #9ca3af',
        }}
      />
      {/* Viewport rectangle */}
      <div
        style={{
          position: 'absolute',
          left: vpLeft,
          top: vpTop,
          width: vpW,
          height: vpH,
          border: '2px solid #3b82f6',
          background: 'rgba(59,130,246,0.1)',
          boxShadow: '0 0 0 1px rgba(59,130,246,0.3)',
          borderRadius: 2,
        }}
      />
      {/* Label */}
      <span
        className="absolute text-white font-semibold"
        style={{ top: 3, left: 5, fontSize: 9, textShadow: '0 1px 2px rgba(0,0,0,0.5)', pointerEvents: 'none' }}
      >
        Navigator
      </span>
    </div>
  );
}

export default CanvasArea;
